/**
 * The device's copy of the catalogue (`D194`, `GET /catalog/snapshot`).
 *
 * ── WHERE IT LIVES, AND WHY NOT IN THE QUERY CACHE ──────────────────────────
 * One value per locale in IndexedDB, under its own key — never inside the
 * persisted Query cache, which is ONE value re-serialised on every change and
 * parsed at every launch (`persist.ts`): a megabyte of catalogue in it would
 * be paid on every write the app makes. Like that cache it is disposable — an
 * evicted copy is fetched again — and it belongs to the device, not to an
 * account, so a sign-out keeps it.
 *
 * ── WHEN IT IS FETCHED ─────────────────────────────────────────────────────
 * {@link refreshCatalogueSnapshot} runs from Today (the screen every launch
 * lands on) at most once a day per locale, sending the version it holds; an
 * unchanged catalogue is a 304 and costs a round trip. Offline it does
 * nothing and the held copy stands.
 *
 * ── WHAT READS IT ──────────────────────────────────────────────────────────
 * The exercise sheet draws an exercise it has never opened from it while the
 * detail is on the wire or unreachable, and search finds exercises in it
 * offline. Online, both read the server as before: the copy carries no
 * instructions and ranks nothing.
 */
import {
  type CatalogueSnapshotRead,
  fetchCatalogueSnapshot,
} from "@repo/client/data/catalog";
import type { AuthorizedFetcher } from "@repo/client/data/core";
import type {
  CatalogueLocale,
  CatalogueSnapshotItem,
  ExerciseDetailOutput,
} from "@repo/contract/catalog";
import { get, set } from "idb-keyval";
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";

/** One locale's copy, as stored. */
export interface StoredSnapshot {
  /** Epoch ms of the last answer from the server, 200 or 304. */
  readonly checkedAt: number;
  readonly items: readonly CatalogueSnapshotItem[];
  readonly version: string;
}

/** How often a held copy is checked against the server. */
export const SNAPSHOT_MAX_AGE_MS = 24 * 3_600_000;

const keyOf = (locale: CatalogueLocale) => `mypocketgym.catalogue.v1.${locale}`;

/** What the copy needs of storage; `idb-keyval` in the app. */
export interface SnapshotStorage {
  read(key: string): Promise<StoredSnapshot | undefined>;
  write(key: string, value: StoredSnapshot): Promise<void>;
}

const idbStorage: SnapshotStorage = {
  read: (key) => get<StoredSnapshot>(key),
  write: (key, value) => set(key, value),
};

/** The copies in memory, by locale, and who is listening. */
export function createSnapshotStore(storage: SnapshotStorage = idbStorage) {
  const held = new Map<CatalogueLocale, StoredSnapshot | null>();
  const loading = new Map<CatalogueLocale, Promise<StoredSnapshot | null>>();
  const refreshing = new Map<CatalogueLocale, Promise<void>>();
  const listeners = new Set<() => void>();
  const notify = () => {
    for (const listener of listeners) {
      listener();
    }
  };

  const load = (locale: CatalogueLocale): Promise<StoredSnapshot | null> => {
    let pending = loading.get(locale);
    if (pending === undefined) {
      pending = storage.read(keyOf(locale)).then(
        (stored) => {
          held.set(locale, stored ?? null);
          notify();
          return stored ?? null;
        },
        () => {
          held.set(locale, null);
          return null;
        }
      );
      loading.set(locale, pending);
    }
    return pending;
  };

  const store = async (locale: CatalogueLocale, value: StoredSnapshot) => {
    held.set(locale, value);
    notify();
    await storage.write(keyOf(locale), value).catch((error: unknown) => {
      console.warn("[data] the catalogue copy was not stored", error);
    });
  };

  return {
    /** The copy in memory; `undefined` until read, `null` when none. */
    current: (locale: CatalogueLocale) => held.get(locale),
    load,
    /**
     * Check the held copy against the server, once at a time per locale and
     * only when it is older than {@link SNAPSHOT_MAX_AGE_MS}. A failure (the
     * network, a 5xx) keeps the held copy and is retried next time.
     */
    refresh(
      locale: CatalogueLocale,
      fetchSnapshot: (held?: string) => Promise<CatalogueSnapshotRead>,
      now: number = Date.now()
    ): Promise<void> {
      let pending = refreshing.get(locale);
      if (pending === undefined) {
        pending = (async () => {
          const stored = await load(locale);
          if (stored !== null && now - stored.checkedAt < SNAPSHOT_MAX_AGE_MS) {
            return;
          }
          const answer = await fetchSnapshot(stored?.version);
          if (answer.kind === "unchanged" && stored !== null) {
            await store(locale, { ...stored, checkedAt: now });
          } else if (answer.kind === "fresh") {
            await store(locale, {
              checkedAt: now,
              items: answer.snapshot.items,
              version: answer.snapshot.version,
            });
          }
        })()
          .catch((error: unknown) => {
            console.warn("[data] the catalogue copy was not refreshed", error);
          })
          .finally(() => {
            refreshing.delete(locale);
          });
        refreshing.set(locale, pending);
      }
      return pending;
    },
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export const snapshotStore = createSnapshotStore();

/** Refresh the copy for `locale` (see the header). Never throws. */
export function refreshCatalogueSnapshot(
  api: AuthorizedFetcher,
  locale: CatalogueLocale
): Promise<void> {
  return snapshotStore.refresh(locale, (held) =>
    fetchCatalogueSnapshot(api, locale, held)
  );
}

/**
 * Today's part (header): refresh the copy in the app's catalogue language
 * once the screen is up and the device is online. Re-runs when the network
 * comes back, which is a no-op inside the day.
 */
export function useCatalogueSnapshotRefresh(
  api: AuthorizedFetcher,
  locale: CatalogueLocale,
  online: boolean
): void {
  useEffect(() => {
    if (online) {
      refreshCatalogueSnapshot(api, locale).catch(() => undefined);
    }
  }, [api, locale, online]);
}

/** The held copy's items, read from disk on first use; `undefined` until. */
export function useCatalogueSnapshot(
  locale: CatalogueLocale
): readonly CatalogueSnapshotItem[] | undefined {
  useEffect(() => {
    snapshotStore.load(locale).catch(() => undefined);
  }, [locale]);
  const held = useSyncExternalStore(
    snapshotStore.subscribe,
    () => snapshotStore.current(locale),
    () => snapshotStore.current(locale)
  );
  return held?.items;
}

/**
 * Each exercise's cut-out (`D196`), by id, from the held copy — for the rows
 * whose own data does not carry one: a plan line, a swap candidate, a line
 * of the workout overview (`D199`). `undefined` while the copy loads and for
 * an exercise without a cut-out; the row keeps its poster either way.
 *
 * The map is built once per copy, not per row: a plan screen of twenty lines
 * would otherwise scan three thousand items twenty times a render.
 */
export function useCutoutLookup(
  locale: CatalogueLocale
): (exerciseId: string) => string | undefined {
  const items = useCatalogueSnapshot(locale);
  const byId = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items ?? []) {
      if (item.cutoutHash) {
        map.set(item.id, item.cutoutHash);
      }
    }
    return map;
  }, [items]);
  return useCallback((exerciseId: string) => byId.get(exerciseId), [byId]);
}

/**
 * The held copy itself: `undefined` until read from disk, `null` when this
 * device has none (never online since install, or evicted) — the builder
 * tells "loading" from "not downloaded" by it (`D197`).
 */
export function useCatalogueSnapshotState(
  locale: CatalogueLocale
): StoredSnapshot | null | undefined {
  useEffect(() => {
    snapshotStore.load(locale).catch(() => undefined);
  }, [locale]);
  return useSyncExternalStore(
    snapshotStore.subscribe,
    () => snapshotStore.current(locale),
    () => snapshotStore.current(locale)
  );
}

/**
 * One exercise of the copy as a detail the sheet can draw: everything but
 * the instructions and the coaching tip, which the copy does not carry, and
 * the second demonstration, which it does not know about. Drawn only while
 * the real detail is on the wire or unreachable.
 */
export function snapshotDetail(
  item: CatalogueSnapshotItem
): ExerciseDetailOutput {
  return {
    category: item.category,
    clips: {
      female: item.clip?.gender === "female" ? item.clip : null,
      male: item.clip?.gender === "male" ? item.clip : null,
    },
    coachingTip: null,
    difficultyLevel: item.difficultyLevel,
    equipment: item.equipment,
    id: item.id,
    instructions: [],
    machine: null,
    muscles: item.muscles,
    name: item.name,
    secondaryEquipment: null,
    ...(item.tracks === undefined ? {} : { tracks: item.tracks }),
    // The two second-layer fields a card carries (`D196`); the frames, the demo and
    // the anatomical muscles are the detail's alone and wait for it.
    ...(item.loadMode === undefined ? {} : { loadMode: item.loadMode }),
    ...(item.cutoutHash === undefined ? {} : { cutoutHash: item.cutoutHash }),
    // `D232`, carried by the copy only where known.
    ...(item.laterality === undefined ? {} : { laterality: item.laterality }),
  };
}
